'use client';

import { useState, useRef, useEffect, PropsWithChildren } from 'react';
import { createPortal } from 'react-dom';
import {
  useForm,
  useFormState,
  type Control,
  type Path,
  type UseFormReturn
} from 'react-hook-form';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ContactRoleBadge, NonProvingBadge } from '../contact-role-badge';
import { User, Building2, Edit2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from '@/lib/toast';
import type { Contact } from '@/lib/services/contacts/types';
import { CreateContactRequestSchema } from '@/lib/services/contacts/types';
import { toContactCreatePayload } from '../contact-create-payload';
import { ContactForm } from './ContactForm';
import { FormActions } from '@/components/inventory/inventory-form-card/shared/FormActions';
import type { ContactType } from './ContactTypeSelector';
import {
  getContactDisplayName,
  getContactType,
  hasRequiredContactName
} from '@/lib/services/contacts/utils';

export interface ContactFormCardProps extends PropsWithChildren {
  contact: Partial<Contact>;
  mode?: 'read' | 'edit';
  onEdit?: () => void;
  onSave?: (contact: Partial<Contact>) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  icon?: React.ReactNode;
  className?: string;
  enableClickToCopy?: boolean;
  hideEditButton?: boolean;
  customAction?: React.ReactNode;
  isSubmitting?: boolean;
  hideBankAccount?: boolean;
  showRoles?: boolean;
  /**
   * Whether the matter is estate-administration. Threaded to `RoleSection` and
   * the read-mode role badges so estate matters show LPR / Beneficiary and
   * non-estate matters show the client-only Role control + Client badge
   * (LF-5788). Defaults to `true`.
   */
  isEstate?: boolean;
  /**
   * The matter this contact is being edited on, when there is one.
   *
   * Only used to surface the contact's PMS binding + its explicit unbind action, which is
   * meaningful solely per `(contact, matter)`. Omitted on the surfaces that reuse this card with no
   * matter behind it — the global contacts list, and the AI-review cards, which stage *proposed*
   * contacts that are not filed anywhere yet.
   */
  matterId?: number;
  showMatterNotes?: boolean;
  showFileNote?: boolean;
  headerContent?: React.ReactNode;
  /**
   * Show the "Other names & addresses" editor for a saved contact (LF-5679).
   * Off by default so the AI-review cards, which stage *proposed* contacts
   * through this same component, never expose live child-row writes.
   */
  showIdentityVariants?: boolean;
  /** Defaults to "Delete" (via `FormActions`) — pass a more specific label
   *  where that's ambiguous, e.g. "Delete contact" (LF-5547). */
  deleteLabel?: string;
  /**
   * When true, run schema validation and surface errors. Cards that fail
   * validation auto-expand to edit mode and gain destructive border styling.
   * Mirrors the same prop on `InventoryFormCard` so the AI-import review
   * flow can highlight every invalid card on submit, not just hint via the
   * top banner.
   */
  forceShowErrors?: boolean;
  /**
   * External preview-time validation message supplied by the parent (e.g.
   * "Missing last name"). Triggers the same auto-expand + destructive border
   * as `forceShowErrors` and renders inline above the form fields.
   */
  forceErrorMessage?: string;
  /**
   * Reports whether the user has actually edited the form (see the doc
   * comment on `FormActionsFooter`'s `hasUserEdits` below). Used by a
   * caller with its own unsaved-changes warning — e.g. `ContactDetailsDrawer`
   * (LF-5547, mirroring the inventory drawer's LF-5542).
   */
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * When provided, the Save/Cancel/Delete row portals into this DOM node
   * instead of rendering inline — for a caller whose scrollable body would
   * otherwise bury it. Mirrors `InventoryFormCard`'s `actionsContainer`
   * (LF-5542). Omitted (the default), renders exactly as it always has.
   */
  actionsContainer?: HTMLElement | null;
}

/**
 * Form-actions footer broken out so the `useFormState` subscription (for
 * `dirtyFields`) only re-renders the footer, not the whole edit form.
 */
function FormActionsFooter({
  control,
  hasTypeChanged,
  hasInteracted,
  isSubmitting,
  onSave,
  onCancel,
  onDelete,
  deleteLabel,
  onDirtyChange,
  actionsContainer
}: {
  control: Control<Partial<Contact>>;
  hasTypeChanged: boolean;
  hasInteracted: boolean;
  isSubmitting: boolean;
  onSave: () => void;
  onCancel: () => void;
  onDelete?: () => void;
  deleteLabel?: string;
  onDirtyChange?: (isDirty: boolean) => void;
  actionsContainer?: HTMLElement | null;
}) {
  const { dirtyFields } = useFormState({ control });

  // Whether the user has actually edited the form — NOT RHF's `isDirty`,
  // which flips true for legitimate programmatic writes some sections make
  // without user input (e.g. `RoleSection`'s effect resetting non-proving
  // state when the role stops being "executor"). `hasTypeChanged` is already
  // only ever set from a genuine toggle click (`handleTypeChange`), so it's
  // safe to OR in directly. See `InventoryFormCard`'s `DirtyStateReporter`
  // for the same fix on the inventory side (LF-5542).
  const hasUserEdits =
    hasInteracted && (Object.keys(dirtyFields).length > 0 || hasTypeChanged);

  useEffect(() => {
    onDirtyChange?.(hasUserEdits);
  }, [hasUserEdits, onDirtyChange]);

  const actions = (
    <FormActions
      onCancel={onCancel}
      onSave={onSave}
      hasChanges={hasUserEdits}
      disabled={!hasUserEdits || isSubmitting}
      isSubmitting={isSubmitting}
      onDelete={onDelete}
      deleteLabel={deleteLabel}
    />
  );

  if (!actionsContainer) return actions;

  return createPortal(
    <div className="shrink-0 border-t px-6 py-4">{actions}</div>,
    actionsContainer
  );
}

export function ContactFormCard({
  children,
  contact,
  mode: initialMode = 'read',
  onEdit,
  onSave,
  onCancel,
  onDelete,
  icon,
  className,
  enableClickToCopy = false,
  hideEditButton = false,
  customAction,
  isSubmitting = false,
  hideBankAccount = false,
  showRoles = true,
  isEstate = true,
  matterId,
  showMatterNotes = false,
  showFileNote = false,
  headerContent,
  deleteLabel,
  forceShowErrors = false,
  forceErrorMessage,
  onDirtyChange,
  actionsContainer,
  showIdentityVariants = false
}: ContactFormCardProps) {
  const [mode, setMode] = useState<'read' | 'edit'>(initialMode);

  // Set by a capture-phase DOM listener on the card the first time the user
  // genuinely interacts with it — used by `FormActionsFooter` to distinguish
  // a real edit from a programmatic auto-fill (e.g. `RoleSection`'s
  // non-proving reset). Mirrors `InventoryFormCard` (LF-5542).
  const [hasInteracted, setHasInteracted] = useState(false);

  const initialContactType = getContactType(contact);
  const [contactType, setContactType] =
    useState<ContactType>(initialContactType);
  const [hasTypeChanged, setHasTypeChanged] = useState(false);

  const savedIndividualFieldsRef = useRef<{
    firstName?: string;
    middleName?: string;
    lastName?: string;
    alias?: string;
    dateOfBirth?: string;
    dateOfDeath?: string;
  }>({
    firstName: contact.firstName,
    middleName: contact.middleName ?? undefined,
    lastName: contact.lastName,
    alias: contact.alias ?? undefined,
    dateOfBirth: contact.dateOfBirth ?? undefined,
    dateOfDeath: contact.dateOfDeath ?? undefined
  });

  const savedOrganisationFieldsRef = useRef<{
    organisationName?: string;
  }>({
    organisationName: contact.organisationName ?? undefined
  });

  // Derive the UI-only `isMinor` toggle from the persisted `isOfAge` field.
  // The backend stores minor status as `isOfAge` (false = minor); the form and
  // read-mode display bind to `isMinor`. onSubmit converts `isMinor → isOfAge`
  // on the way out, but without this reverse mapping a contact saved as a minor
  // re-loads with the toggle unticked and the minor status hidden — both in the
  // Will-extraction review card and the Contacts drawer. Only derive when the
  // form value isn't already explicitly set, so an in-flight edit wins.
  //
  // Also back-fill `isDeceased` from a legacy `dateOfDeath` when the flag is
  // absent (older records / mock data predating the first-class boolean), so a
  // contact with only a date still loads with the checkbox ticked and its date
  // field revealed.
  const normalizedContact: Partial<Contact> = (() => {
    const next: Partial<Contact> = { ...contact };
    if (contact.isMinor === undefined && typeof contact.isOfAge === 'boolean') {
      next.isMinor = !contact.isOfAge;
    }
    if (contact.isDeceased === undefined || contact.isDeceased === null) {
      next.isDeceased = Boolean(contact.dateOfDeath);
    }
    return next;
  })();

  const form: UseFormReturn<Partial<Contact>> = useForm<Partial<Contact>>({
    defaultValues: normalizedContact,
    mode: 'onSubmit'
    // No resolver: validation runs manually in onSubmit (below) after
    // transforming the form value into the shape `CreateContactRequestSchema`
    // expects. The form holds UI-only fields (e.g. `isMinor`) that the schema
    // doesn't accept, so resolver-based validation would reject valid input.
  });

  const onSubmit = async (value: Partial<Contact>) => {
    /*
      Bound to the shared transform rather than restating it (VALE-632): the
      attribute-treatment create form builds its payload from the same rules,
      and "a created contact is identical whichever surface created it" is only
      true for as long as there is one copy of them.
    */
    const transformedValue = toContactCreatePayload(value, contactType);

    const result = CreateContactRequestSchema.safeParse(transformedValue);

    if (!result.success) {
      const errors = result.error.flatten();

      // Surface field errors via RHF so each Controller picks them up; this
      // mirrors the previous external `submitErrors` state but goes through
      // the form's own error system. `keepDirty: false` is the default; we
      // don't pass shouldFocus so subsequent edits clear the errors normally.
      for (const [fieldName, msgs] of Object.entries(errors.fieldErrors)) {
        if (msgs && msgs.length > 0) {
          form.setError(fieldName as Path<Partial<Contact>>, {
            type: 'server',
            message: msgs[0]
          });
        }
      }

      if (errors.formErrors.length > 0) {
        toast.error(errors.formErrors.join('\n'));
      }
      return;
    }

    await onSave?.(result.data as Partial<Contact>);
    setMode('read');
    onDirtyChange?.(false);
  };

  const handleTypeChange = (newType: ContactType) => {
    const currentValues = form.getValues();

    if (newType === 'organisation') {
      savedIndividualFieldsRef.current = {
        firstName:
          currentValues.firstName || savedIndividualFieldsRef.current.firstName,
        middleName:
          currentValues.middleName ??
          savedIndividualFieldsRef.current.middleName,
        lastName:
          currentValues.lastName || savedIndividualFieldsRef.current.lastName,
        alias: currentValues.alias ?? savedIndividualFieldsRef.current.alias,
        dateOfBirth:
          currentValues.dateOfBirth ??
          savedIndividualFieldsRef.current.dateOfBirth,
        dateOfDeath:
          currentValues.dateOfDeath ??
          savedIndividualFieldsRef.current.dateOfDeath
      };

      form.setValue('firstName', '');
      form.setValue('middleName', '');
      form.setValue('lastName', '');
      form.setValue('alias', '');
      form.setValue('dateOfBirth', '');
      form.setValue('dateOfDeath', '');

      form.setValue(
        'organisationName',
        savedOrganisationFieldsRef.current.organisationName || ''
      );
    } else {
      savedOrganisationFieldsRef.current = {
        organisationName:
          currentValues.organisationName ??
          savedOrganisationFieldsRef.current.organisationName
      };

      form.setValue('organisationName', '');

      form.setValue(
        'firstName',
        savedIndividualFieldsRef.current.firstName || ''
      );
      form.setValue(
        'middleName',
        savedIndividualFieldsRef.current.middleName || ''
      );
      form.setValue(
        'lastName',
        savedIndividualFieldsRef.current.lastName || ''
      );
      form.setValue('alias', savedIndividualFieldsRef.current.alias || '');
      form.setValue(
        'dateOfBirth',
        savedIndividualFieldsRef.current.dateOfBirth || ''
      );
      form.setValue(
        'dateOfDeath',
        savedIndividualFieldsRef.current.dateOfDeath || ''
      );
    }
    setContactType(newType);
    setHasTypeChanged(newType !== initialContactType);
  };

  const handleEdit = () => {
    setMode('edit');
    onEdit?.();
  };

  const isContactEmpty = (vals: Partial<Contact>): boolean => {
    const hasName =
      contactType === 'organisation'
        ? Boolean(vals.organisationName?.trim())
        : Boolean(vals.firstName?.trim() || vals.lastName?.trim());

    const hasEmail = Boolean(vals.email?.trim());
    const hasPhone = Boolean(vals.phoneNumber?.trim());
    const hasAddress =
      vals.address &&
      Object.values(vals.address).some((val) =>
        Boolean(val?.toString().trim())
      );
    const hasBankAccount =
      vals.bankAccount &&
      Object.values(vals.bankAccount).some((val) =>
        Boolean(val?.toString().trim())
      );

    return !hasName && !hasEmail && !hasPhone && !hasAddress && !hasBankAccount;
  };

  const handleCancel = () => {
    const isNewContact = !contact.id;
    const currentValues = form.getValues();
    const isEmpty = isContactEmpty(currentValues);

    if (isNewContact && isEmpty && onDelete) {
      onDelete();
      return;
    }

    form.reset();
    setContactType(initialContactType);
    setHasTypeChanged(false);
    setMode('read');
    onDirtyChange?.(false);
    onCancel?.();
  };

  // Snapshot for read-mode display + collapsible initial state. Re-evaluated
  // when the parent passes a new `contact` prop (post-save refetch). Uses the
  // normalized contact so the read-mode "This contact is a minor" row reflects
  // the persisted `isOfAge` (see `normalizedContact` above).
  const displayValues = normalizedContact;

  // Minimum required name — shared with the AI-import/Scribe/intake review
  // cards (LF-5427). Used to gate the forceShowErrors auto-expand below.
  const hasRequiredName = hasRequiredContactName(
    displayValues as Record<string, unknown>
  );

  // Auto-expand to edit mode when the parent forces error display, either via
  // `forceShowErrors` (run our own name check) or `forceErrorMessage` (an
  // external preview-time check). Mirrors the InventoryFormCard pattern.
  // See https://react.dev/learn/you-might-not-need-an-effect.
  const [autoExpandKey, setAutoExpandKey] = useState<string | null>(null);
  if (mode === 'read') {
    const shouldExpand =
      !!forceErrorMessage || (forceShowErrors && !hasRequiredName);
    if (shouldExpand) {
      const nextKey = `${forceErrorMessage ?? ''}-${forceShowErrors ? '1' : '0'}`;
      if (autoExpandKey !== nextKey) {
        setAutoExpandKey(nextKey);
        setMode('edit');
        onEdit?.();
      }
    }
  }

  const getTitle = (): string => {
    const displayName = getContactDisplayName(displayValues);
    if (displayName === 'New Contact') {
      return contactType === 'organisation'
        ? 'New Organisation'
        : 'New Contact';
    }
    return displayName;
  };

  const title = getTitle();

  const showDestructiveBorder =
    mode === 'edit' &&
    (!!forceErrorMessage || (forceShowErrors && !hasRequiredName));

  return (
    <Card
      className={cn(
        'border-border bg-card flex w-full flex-col items-end gap-4 rounded-[12px] border p-4.5 text-sm',
        showDestructiveBorder && 'border-destructive border-2',
        className
      )}
      data-validation-error={showDestructiveBorder || undefined}
      onPointerDownCapture={() => setHasInteracted(true)}
      onKeyDownCapture={() => setHasInteracted(true)}
    >
      {!!forceErrorMessage && mode === 'edit' && (
        <div
          role="alert"
          className="bg-destructive/5 text-destructive border-destructive/30 flex w-full items-start gap-2 rounded-md border px-3 py-2 text-sm"
        >
          <span className="font-medium">{forceErrorMessage}</span>
        </div>
      )}
      {mode === 'read' ? (
        <>
          <div className="flex w-full items-center justify-between">
            <div className="flex items-center gap-2">
              {icon ||
                (contactType === 'organisation' ? (
                  <Building2 className="text-muted-foreground h-4 w-4" />
                ) : (
                  <User className="text-muted-foreground h-4 w-4" />
                ))}
              {/* Title text is wrapped in a <span> so the <h3> is not a
                  React-managed text leaf: AiCardShell portals its "Proposed
                  by Vale AI" chip into this <h3>, and React 19 warns when a
                  portal container also sets text children (LF-4585). */}
              <h3 className="text-foreground text-base font-medium">
                <span>{title}</span>
              </h3>
              {showRoles && isEstate && (
                <>
                  {displayValues.isLegalPersonalRepresentative && (
                    <ContactRoleBadge role="lpr" className="ml-1" />
                  )}
                  {displayValues.isLegalPersonalRepresentative &&
                    displayValues.isProvingLpr === false && (
                      <NonProvingBadge
                        reason={displayValues.nonProvingReason}
                        className="ml-1"
                      />
                    )}
                  {displayValues.isBeneficiary && (
                    <ContactRoleBadge role="beneficiary" className="ml-1" />
                  )}
                </>
              )}
              {showRoles && !isEstate && displayValues.isClient && (
                <ContactRoleBadge role="client" className="ml-1" />
              )}
            </div>
            {!hideEditButton && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={handleEdit}>
                  <Edit2 className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          <div className="flex w-full flex-col gap-4">
            <ContactForm
              control={form.control}
              values={displayValues}
              mode="read"
              clickToCopy={enableClickToCopy}
              customAction={customAction}
              contactType={contactType}
              onTypeChange={handleTypeChange}
              hideBankAccount={hideBankAccount}
              showRoles={showRoles}
              isEstate={isEstate}
              matterId={matterId}
              showMatterNotes={showMatterNotes}
              showFileNote={showFileNote}
            />
          </div>
        </>
      ) : (
        <>
          <div className="flex w-full flex-col gap-4">
            <ContactForm
              control={form.control}
              values={displayValues}
              mode="edit"
              contactType={contactType}
              onTypeChange={handleTypeChange}
              hideBankAccount={hideBankAccount}
              showRoles={showRoles}
              isEstate={isEstate}
              matterId={matterId}
              showMatterNotes={showMatterNotes}
              showFileNote={showFileNote}
              headerContent={headerContent}
              showIdentityVariants={showIdentityVariants}
            />
          </div>

          <FormActionsFooter
            control={form.control}
            hasTypeChanged={hasTypeChanged}
            hasInteracted={hasInteracted}
            isSubmitting={isSubmitting}
            onSave={form.handleSubmit(onSubmit)}
            onCancel={handleCancel}
            onDelete={onDelete}
            deleteLabel={deleteLabel}
            onDirtyChange={onDirtyChange}
            actionsContainer={actionsContainer}
          />
        </>
      )}

      {children}
    </Card>
  );
}
